import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import type { SuggestedEvidencesPageResponse } from './suggestedEvidencePagination';

type SuggestedInfiniteData = InfiniteData<SuggestedEvidencesPageResponse>;

export const removeEvidenceFromSuggestedInfiniteCache = (
  queryClient: QueryClient,
  ideaId: string,
  evidenceId: string
): void => {
  const queries = queryClient.getQueriesData<SuggestedInfiniteData>({
    predicate: (query) => {
      const key = query.queryKey;
      return (
        Array.isArray(key) &&
        key[0] === 'idea' &&
        key[1] === 'detail' &&
        key[2] === ideaId &&
        key[3] === 'suggested-evidences-infinite'
      );
    },
  });

  queries.forEach(([queryKey, data]) => {
    if (!data?.pages?.length) {
      return;
    }

    let removedCount = 0;
    const updatedPages = data.pages.map((page, pageIndex) => {
      const filteredEvidences = (page?.evidences || []).filter(
        (evidence) => String(evidence?._id) !== String(evidenceId)
      );
      removedCount += (page?.evidences?.length ?? 0) - filteredEvidences.length;

      return {
        ...page,
        evidences: filteredEvidences,
        totalCount:
          pageIndex === 0 && page?.totalCount != null && removedCount > 0
            ? Math.max(0, page.totalCount - removedCount)
            : page?.totalCount,
      };
    });

    const firstPage = updatedPages[0];
    if (firstPage?.totalCount != null) {
      const totalLoaded = updatedPages.reduce(
        (sum, page) => sum + (page.evidences?.length ?? 0),
        0
      );
      updatedPages[0] = {
        ...firstPage,
        hasMore: totalLoaded < firstPage.totalCount,
      };
    }

    queryClient.setQueryData(queryKey, {
      ...data,
      pages: updatedPages,
    });
  });
};

export const flattenSuggestedInfinitePages = (
  data: SuggestedInfiniteData | undefined
): any[] => data?.pages?.flatMap((page) => page?.evidences || []) || [];

export const refetchTabEvidenceTotals = (
  queryClient: QueryClient,
  ideaId: string
): void => {
  const allKeys = queryClient.getQueryCache().getAll().map(q => q.queryKey);
  const tabTotalsKeys = allKeys.filter((key) =>
    Array.isArray(key) &&
    key[0] === 'idea' &&
    key[1] === 'detail' &&
    key[2] === ideaId &&
    key[3] === 'tab-evidence-totals'
  );

  tabTotalsKeys.forEach((queryKey) => {
    queryClient.refetchQueries({ queryKey, exact: true, type: 'all' });
  });
};
